// PAGCanvas.jsx
import React, { useRef, useEffect, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import getPag from '../resource/getPag.js';

export const PAGCanvas = ({
  src = '',
  isPlaying = true,
  onPlay = null,
  onEnd = null,
  onError = null,
  repeat = 1,
  fullscreen = false,
}) => {
  const canvasRef = useRef(null);
  const playerRef = useRef(null);
  const mountedRef = useRef(true);
  const endTimerRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const cleanupPlayer = useCallback(() => {
    const player = playerRef.current;
    if (!player) return;

    if (endTimerRef.current) {
      clearTimeout(endTimerRef.current);
    }

    player.stop?.();
    player.dispose?.();

    playerRef.current = null;
  }, []);

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.style.width = '100%';
    canvas.style.height = '100%';

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.max(1, rect.width * dpr);
    canvas.height = Math.max(1, rect.height * dpr);
  }, []);

  const handleEnd = useCallback(() => {
    if (!mountedRef.current) return;

    // clear timeout
    if (endTimerRef.current) {
      clearTimeout(endTimerRef.current);
      endTimerRef.current = null;
    } else {
      return;
    }

    cleanupPlayer();
    onEnd?.();
  }, [onEnd, cleanupPlayer]);

  const loadPAG = useCallback(async () => {
    const canvas = canvasRef.current;

    if (!canvas || !src || !isPlaying || !mountedRef.current) return;

    cleanupPlayer();
    setError(null);
    setIsLoading(true);

    try {
      const { isPAGSupported, pag } = await getPag({ imageSource: src });

      if (!mountedRef.current) return;

      if (!isPAGSupported || !pag) {
        throw new Error('PAG format not supported');
      }

      resizeCanvas();
      if (canvas.width === 0 || canvas.height === 0) {
        canvas.width = Math.max(1, pag.width || 300);
        canvas.height = Math.max(1, pag.height || 150);
      }

      const player = await pag.getPlayer(canvas);

      if (!mountedRef.current) {
        player?.dispose?.();
        return;
      }

      playerRef.current = player;
      player.setRepeatCount?.(repeat);

      if (player._view?.addListener) {
        player._view.addListener('onAnimationEnd', handleEnd);
      }

      const duration = player.getDuration?.() || pag.duration || 0;
      if (duration > 0) {
        endTimerRef.current = setTimeout(handleEnd, duration + 100);
      }

      player.play?.();
      onPlay?.();

      if (player._view?.flush) {
        player._view.flush();
      }

      setIsLoading(false);
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err.message);
      setIsLoading(false);
      onError?.(err);
    }
  }, [
    src,
    isPlaying,
    repeat,
    onPlay,
    onError,
    cleanupPlayer,
    resizeCanvas,
    handleEnd,
  ]);

  useEffect(() => {
    const timer = setTimeout(loadPAG, 50);
    return () => clearTimeout(timer);
  }, [loadPAG]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      cleanupPlayer();
    };
  }, [cleanupPlayer]);

  return (
    <StyledPAGCanvas
      $isLoading={isLoading}
      $error={error}
      $fullscreen={fullscreen}
    >
      <canvas ref={canvasRef} />
    </StyledPAGCanvas>
  );
};

PAGCanvas.propTypes = {
  src: PropTypes.string,
  isPlaying: PropTypes.bool,
  onPlay: PropTypes.func,
  onEnd: PropTypes.func,
  onError: PropTypes.func,
  repeat: PropTypes.number,
  fullscreen: PropTypes.bool,
};

const StyledPAGCanvas = styled.div`
  width: 100%;
  height: 100%;

  canvas {
    width: 100%;
    height: 100%;
    object-fit: contain;
    transition: opacity 0.3s ease-in-out;
    opacity: ${({ $isLoading, $error }) => {
      if ($isLoading) return 0.7;
      if ($error) return 0.5;
      return 1;
    }};

    ${({ $fullscreen }) =>
      $fullscreen &&
      `
    position: absolute;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
  `}
  }
`;

export default PAGCanvas;
